/**
 * SignalDetailPage tile — full Starlink dish snapshot. Reads straight
 * from the box.starlink field on the snapshot (the same data the map
 * popup uses); no extra fetch.
 *
 * Hidden entirely when the box has no Starlink data (never polled).
 */
import React from "react";

import { DetailCard, DetailRow } from "./DetailCard";
import { fmtAgo, fmtUtc } from "../../lib/format";
import type { Box } from "../../types/boxes";

const fmtBytes = (bps: number | null): string => {
  if (bps == null) return "—";
  const abs = Math.abs(bps);
  if (abs >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (abs >= 1_000) return `${(bps / 1_000).toFixed(1)} kbps`;
  return `${bps.toFixed(0)} bps`;
};

const fmtUptime = (sec: number | null): string => {
  if (sec == null) return "—";
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export const StarlinkCard: React.FC<{ box: Box }> = ({ box }) => {
  const sl = box.starlink;
  const account = box.starlink_account_status;
  const accountBad = account != null && account !== "in_service";
  // Ops-managed account status row, shown in every branch when known.
  const accountRow =
    account != null ? (
      <DetailRow label="Account">
        <span className={accountBad ? "text-error-700 font-semibold" : "text-secondary-700"}>
          {account}
        </span>
      </DetailRow>
    ) : null;

  if (sl == null) {
    // Never polled. Still surface a broken/lost/cancelled account so a
    // replaced dish is actionable even with no telemetry.
    if (!accountBad) return null;
    return (
      <DetailCard title="Starlink">
        <div className="text-xs text-error-700">
          Dish account <strong>{account}</strong> — likely replaced/cancelled; no
          telemetry from this box.
        </div>
      </DetailCard>
    );
  }
  if (!sl.reachable) {
    return (
      <DetailCard title="Starlink">
        <div className="text-xs text-secondary-500">
          Dish not reachable on last poll.
        </div>
        {accountRow}
        {sl.error_message && (
          <div className="text-[11px] text-error-700 mt-1">{sl.error_message}</div>
        )}
        {sl.polled_utc && (
          <div className="text-[11px] text-secondary-500 mt-1">
            tried {fmtAgo(sl.polled_utc)} ago ({fmtUtc(sl.polled_utc)})
          </div>
        )}
      </DetailCard>
    );
  }
  const obs = sl.fraction_obstructed;
  return (
    <DetailCard title="Starlink">
      {accountRow}
      <DetailRow label="State">{sl.state ?? "—"}</DetailRow>
      <DetailRow label="POP latency">
        {sl.pop_ping_latency_ms != null
          ? `${sl.pop_ping_latency_ms.toFixed(1)} ms`
          : "—"}
      </DetailRow>
      <DetailRow label="Down / Up">
        {fmtBytes(sl.downlink_throughput_bps)} /{" "}
        {fmtBytes(sl.uplink_throughput_bps)}
      </DetailRow>
      <DetailRow label="Drop rate">
        {sl.pop_ping_drop_rate != null
          ? `${(sl.pop_ping_drop_rate * 100).toFixed(2)}%`
          : "—"}
      </DetailRow>
      <DetailRow label="Obstruction">
        {obs != null ? (
          <>
            {(obs * 100).toFixed(2)}%
            {sl.currently_obstructed ? (
              <span className="ml-1 text-warning-700 text-[11px] font-semibold">
                obstructed now
              </span>
            ) : null}
          </>
        ) : (
          "—"
        )}
      </DetailRow>
      <DetailRow label="GPS sats">{sl.gps_sats ?? "—"}</DetailRow>
      {sl.router_client_count != null && (
        <DetailRow label="Router clients">
          <div>
            <span>{sl.router_client_count}</span>
            {sl.router_clients && sl.router_clients.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {sl.router_clients.map((cl, i) => {
                  const isRouter = cl.role.toUpperCase() === "CONTROLLER";
                  return (
                    <li key={`${cl.name}-${i}`} className="text-[11px] text-secondary-600">
                      {cl.name || "(unnamed)"}
                      {isRouter ? (
                        <span className="ml-1 text-secondary-400">— Starlink router</span>
                      ) : (
                        <span className="ml-1 text-secondary-400">— {cl.conn}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DetailRow>
      )}
      <DetailRow label="Uptime">{fmtUptime(sl.uptime_seconds)}</DetailRow>
      {sl.active_alerts && (
        <DetailRow label="Active alerts">
          <span className="text-warning-700 text-xs">{sl.active_alerts}</span>
        </DetailRow>
      )}
      <DetailRow label="Hardware / Software">
        {sl.hardware_version ?? "—"} / {sl.software_version ?? "—"}
      </DetailRow>
      {sl.polled_utc && (
        <p className="text-[11px] text-secondary-500 mt-2">
          polled {fmtAgo(sl.polled_utc)} ago ({fmtUtc(sl.polled_utc)})
        </p>
      )}
    </DetailCard>
  );
};

export default StarlinkCard;
